/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Theme,
  BaseProvider,
  createTheme,
  darkThemePrimitives,
  lightThemePrimitives,
} from "baseui";
import { BackgroundVariant } from "@xyflow/react";
import { ThemeType, themes, ThemeNames } from "./Theme";

export const NO_CANVAS_BACKGROUND = "none";
export type CanvasBackground = BackgroundVariant | typeof NO_CANVAS_BACKGROUND;

type ThemeContextType = {
  currentTheme: ThemeType;
  currentThemeName: keyof typeof themes;
  setTheme: (themeName: keyof typeof themes) => void;
  canvasBackground: CanvasBackground;
  setCanvasBackground: (backgroundVariant: CanvasBackground) => void;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: themes.light,
  currentThemeName: "light",
  setTheme: () => {},
  canvasBackground: BackgroundVariant.Dots,
  setCanvasBackground: () => undefined,
});

// Need to override colors in the default BaseWeb themeing to take care
// of components we can't reach such as the User profile icon color
const mapDexterThemeToBaseWeb = (dexterTheme: ThemeType): Theme => {
  const primitives =
    dexterTheme === themes[ThemeNames.DARK] ||
    dexterTheme === themes[ThemeNames.COLORBLIND_DARK]
      ? darkThemePrimitives
      : lightThemePrimitives;

  return createTheme({
    ...primitives,
    colors: {
      ...primitives,

      // Buttons
      buttonPrimaryFill: dexterTheme.colors.primaryButtonColor,
      buttonPrimaryText: dexterTheme.colors.primaryElementFontColor,
      buttonTertiaryFill: dexterTheme.colors.backgroundTertiary,
      buttonTertiaryText: dexterTheme.colors.text,
      buttonSecondaryFillDisabled: "transparent",

      // Avatar/initials (profile icon)
      avatarBackground: dexterTheme.colors.primaryBlue,
      avatarForeground: dexterTheme.colors.primaryElementFontColor,

      // AppNavBar, header, etc.
      menuFillHover: dexterTheme.colors.selectionBackgroundPrimaryDark,
      headerNavigationFill: dexterTheme.colors.headerColor,
      headerNavigationText: dexterTheme.colors.headerTextColor,

      // General
      backgroundPrimary: dexterTheme.colors.background,
      backgroundSecondary: dexterTheme.colors.backgroundTertiary,
      contentPrimary: dexterTheme.colors.text,
    },
  });
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState<keyof typeof themes>(
    ThemeNames.DARK
  );

  const [canvasBackground, setCanvasBackgroundState] =
    useState<CanvasBackground>(() => {
      const saved = localStorage.getItem("user-canvas-background");
      return (saved as CanvasBackground) ?? BackgroundVariant.Dots;
    });

  const setCanvasBackground = (backgroundVariant: CanvasBackground): void => {
    setCanvasBackgroundState(backgroundVariant);
    localStorage.setItem("user-canvas-background", backgroundVariant);
  };

  const setTheme = (themeName: keyof typeof themes): void => {
    setCurrentThemeName(themeName);
    localStorage.setItem("user-theme", themeName as string); // Also persist on change
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("user-theme");
    if (savedTheme && savedTheme in themes) {
      setTheme(savedTheme as keyof typeof themes);
    }
  }, []);

  const dexterTheme = themes[currentThemeName];

  // Memoize to avoid re-creating theme objects on every render
  const baseWebTheme = React.useMemo(
    () => mapDexterThemeToBaseWeb(dexterTheme),
    [dexterTheme]
  );

  return (
    <ThemeContext.Provider
      value={{
        currentTheme: dexterTheme,
        currentThemeName,
        setTheme,
        canvasBackground,
        setCanvasBackground,
      }}
    >
      <BaseProvider theme={baseWebTheme}>{children}</BaseProvider>
    </ThemeContext.Provider>
  );
};

export const useDexterTheme = (): ThemeContextType => useContext(ThemeContext);
